import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

const faqs = [
  {
    question: 'Wie lange dauert eine Dachsanierung?',
    answer: 'Die Dauer hängt vom Umfang der Arbeiten ab. Eine komplette Dachsanierung dauert in der Regel 1-3 Wochen. Nach einer kostenlosen Begutachtung vor Ort können wir Ihnen einen genauen Zeitrahmen nennen.'
  },
  {
    question: 'Welche Dacharten bieten Sie an?',
    answer: 'Wir realisieren alle gängigen Dacharten: Satteldächer, Walmdächer, Flachdächer, Pultdächer und mehr. Dabei verwenden wir verschiedenste Materialien wie Ziegel, Schiefer, Metall oder moderne Dachbahnen.'
  },
  {
    question: 'Übernehmen Sie auch Notfall-Reparaturen?',
    answer: 'Ja, bei Sturmschäden oder akuten Undichtigkeiten sind wir schnellstmöglich für Sie da. Kontaktieren Sie uns telefonisch, und wir kümmern uns umgehend um die Schadensbehebung.'
  },
  {
    question: 'Bieten Sie eine Gewährleistung auf Ihre Arbeiten?',
    answer: 'Selbstverständlich! Als Meisterbetrieb gewährleisten wir höchste Qualität. Auf unsere Arbeiten erhalten Sie die gesetzliche Gewährleistung sowie zusätzliche Garantien je nach verwendeten Materialien und Leistungsumfang.'
  },
  {
    question: 'Wie erhalte ich ein Angebot?',
    answer: 'Kontaktieren Sie uns telefonisch, per E-Mail oder über unser Kontaktformular. Wir vereinbaren einen Termin zur Begutachtung vor Ort und erstellen Ihnen anschließend ein detailliertes, unverbindliches Angebot.'
  },
  {
    question: 'Arbeiten Sie auch bei schlechtem Wetter?',
    answer: 'Bei leichtem Regen und moderaten Wetterbedingungen können viele Arbeiten durchgeführt werden. Bei extremen Wetterlagen wie Sturm oder Starkregen pausieren wir aus Sicherheitsgründen. Wir informieren Sie rechtzeitig und stimmen neue Termine ab.'
  }
];

export const FAQSection = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section ref={ref} className="py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-red-100 to-transparent opacity-40 rounded-full blur-3xl"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Häufig gestellte
            <span className="block bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              Fragen
            </span>
          </h2>
          <p className="text-xl text-gray-700">
            Hier finden Sie Antworten auf die wichtigsten Fragen rund um unsere Leistungen
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-gray-50 rounded-xl px-6 border-2 border-gray-100 hover:border-red-200 transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-red-600 py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Additional CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <p className="text-gray-700 mb-6">Haben Sie weitere Fragen?</p>
          <motion.button
            onClick={() => document.getElementById('kontakt').scrollIntoView({ behavior: 'smooth' })}
            className="relative group px-8 py-3 bg-gradient-to-r from-red-600 to-red-800 text-white font-bold rounded-lg overflow-hidden"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10">Jetzt kontaktieren</span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-900"
              initial={{ x: '-100%' }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.3 }}
            />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};